import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ClipboardList,
  CreditCard,
  DollarSign,
  FileText,
  FolderOpen,
  Home,
  MapPin,
  ScanSearch,
  ScrollText,
  Shield,
  User,
  Users,
} from "lucide-react";

import { cn } from "../../lib/utils";
// @ts-ignore - JS module without types
import { fetchFulfillmentPendingCount } from "../for-service-provider/servicerFulfillmentApi";
import {
  readSessionFlag,
  rememberSessionFlag,
  useCarPortfolioEnabled,
} from "../for-service-provider/useCarPortfolioEnabled";
import { getAPIPayload } from "../../apis/getApi";
import epLogo from "../../assets/new_ui_images/equity-protect-logo-full.png";
import SidebarLogo from "../../assets/icons/ep-icon-blue.png";
import CallPilotChatButton from "../common/CallPilotChatButton";
import { ServicerEpAwareDashboardButton } from "./ServicerEpAwareDashboardButton";

const primaryNav = [
  { name: "Unlock Request(s)", href: "/documents", icon: FileText },
  { name: "Risk Assessments", href: "/risk-assessments", icon: ClipboardList },
  { name: "Clients", href: "/clients", icon: Users },
  {
    name: "Territory",
    href: "/fulfillment-assignments",
    icon: MapPin,
  },
  { name: "Service fee's", href: "/commissions", icon: DollarSign },
];

const CLOSING_ASSIGNMENTS_FLAG = "carClosingAssignments";

const personalNav = [
  { name: "Properties", href: "/servicer-properties", icon: Home },
  { name: "Subscriptions", href: "/servicer-subscriptions", icon: CreditCard },
  { name: "Scans", href: "/servicer-scans", icon: ScanSearch },
  { name: "Documents", href: "/servicer-documents", icon: FolderOpen },
  { name: "Allow Access", href: "/servicer-allow-access", icon: Shield },
  { name: "Logs", href: "/servicer-logs", icon: ScrollText },
  { name: "Profile", href: "/servicer-profile", icon: User },
];

export const ServicerDashboardSidebar = ({
  isSidebarOpen = true,
  onNavigate,
}: {
  isSidebarOpen?: boolean;
  onNavigate?: () => void;
}) => {
  const location = useLocation();
  const [offerCount, setOfferCount] = useState(0);
  const [fulfillmentEnabled, setFulfillmentEnabled] = useState(false);
  const [agreementRequired, setAgreementRequired] = useState(false);
  const { carPortfolioEnabled, loading: carNavLoading } = useCarPortfolioEnabled();
  // Remembered like the CAR answer, so an escrow account keeps a steady menu.
  const [closingAssignments, setClosingAssignments] = useState(
    () => readSessionFlag(CLOSING_ASSIGNMENTS_FLAG) === true,
  );
  const [closingAssignmentsKnown, setClosingAssignmentsKnown] = useState(
    () => readSessionFlag(CLOSING_ASSIGNMENTS_FLAG) !== null,
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetchFulfillmentPendingCount();
        if (cancelled || !response?.success) return;
        setOfferCount(Number(response.data?.offered_count) || 0);
        setFulfillmentEnabled(response.data?.enabled === true);
        setAgreementRequired(response.data?.agreement_required === true);
      } catch {
        if (!cancelled) {
          setOfferCount(0);
          setFulfillmentEnabled(false);
          setAgreementRequired(false);
        }
      }
    };
    load();
    getAPIPayload({
      url: "service-provider/car-closings",
      token: true,
    })
      .then((response) => {
        if (!cancelled) {
          const hasClosings =
            Boolean(response?.data?.viewer_is_escrow) ||
            (response?.data?.rows || []).length > 0;
          setClosingAssignments(hasClosings);
          rememberSessionFlag(CLOSING_ASSIGNMENTS_FLAG, hasClosings);
          setClosingAssignmentsKnown(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Keep a remembered answer rather than flipping the menu on a failed call.
          if (readSessionFlag(CLOSING_ASSIGNMENTS_FLAG) === null) {
            setClosingAssignments(false);
          }
          setClosingAssignmentsKnown(true);
        }
      });
    const timer = setInterval(load, 120000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === "/documents") {
      return (
        location.pathname === "/documents" ||
        location.pathname === "/request-document" ||
        location.pathname === "/search-property"
      );
    }
    if (path === "/fulfillment-assignments") {
      return (
        location.pathname.startsWith("/fulfillment-assignments") ||
        location.pathname.startsWith("/fulfillment/clients")
      );
    }
    return location.pathname.startsWith(path);
  };

  const renderSectionHeader = (
    label: string,
    highlighted = false,
    className?: string,
  ) =>
    isSidebarOpen ? (
      <p
        className={cn(
          "mb-2 px-3 text-xs font-semibold uppercase tracking-wide",
          highlighted ? "text-secondary" : "text-muted-foreground",
          className,
        )}
      >
        {label}
      </p>
    ) : null;

  const carProfessionalNav = [
    { name: "Clients", href: "/clients", icon: Users },
    { name: "Risk Assessments", href: "/risk-assessments", icon: ClipboardList },
    { name: "Profile", href: "/servicer-profile", icon: User },
    ...(closingAssignments
      ? [{ name: "Closing assignments", href: "/closing-assignments", icon: FolderOpen }]
      : []),
  ];
  const carPersonalNav = personalNav.filter(
    (item) => item.href !== "/servicer-profile",
  );

  const escrowOnly =
    !carPortfolioEnabled && closingAssignments;
  // Which menu to show is known (remembered or answered); until then show none,
  // so no option from the wrong menu flashes.
  const navReady = !carNavLoading && closingAssignmentsKnown;
  const escrowNav = [
    { name: "Closing assignments", href: "/closing-assignments", icon: FolderOpen },
    { name: "Profile", href: "/servicer-profile", icon: User },
  ];

  const visiblePrimaryNav = escrowOnly
    ? escrowNav
    : carPortfolioEnabled
    ? carProfessionalNav
    : primaryNav.filter(
        (item) =>
          item.href !== "/fulfillment-assignments" || fulfillmentEnabled,
      );

  const renderLink = (item: (typeof primaryNav)[number]) => {
    const active = isActive(item.href);
    const badge =
      item.href === "/fulfillment-assignments" && offerCount > 0
        ? offerCount
        : 0;
    const needsAgreement =
      item.href === "/fulfillment-assignments" && agreementRequired;

    return (
      <li key={item.href}>
        <Link
          to={item.href}
          onClick={onNavigate}
          className={cn(
            "flex items-start gap-3 rounded-lg transition-colors",
            active
              ? "bg-secondary/10 text-secondary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
            isSidebarOpen
              ? "px-3 py-2.5 text-sm font-medium w-full min-w-0"
              : "px-0 py-2.5 justify-center w-full",
          )}
          title={
            needsAgreement
              ? "Program agreement acknowledgment required"
              : item.name
          }
        >
          <span className="relative shrink-0">
            <item.icon className="h-5 w-5 shrink-0 mt-0.5" />
            {badge > 0 && !isSidebarOpen ? (
              <span className="absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white">
                {badge}
              </span>
            ) : null}
          </span>
          {isSidebarOpen ? (
            <span className="flex-1 min-w-0 whitespace-normal break-words leading-5">
              {item.name}
            </span>
          ) : null}
          {badge > 0 && isSidebarOpen ? (
            <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-white">
              {badge}
            </span>
          ) : null}
          {needsAgreement && badge === 0 && isSidebarOpen ? (
            <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              Agreement
            </span>
          ) : null}
        </Link>
      </li>
    );
  };

  return (
    <aside
      className={cn(
        "bg-card border-r border-border flex flex-col h-screen fixed left-0 top-0 transition-all duration-200 z-20",
        isSidebarOpen ? "w-64" : "w-16",
      )}
    >
      <div
        className={cn(
          "border-b border-border flex items-center",
          isSidebarOpen ? "p-4 justify-start" : "p-2 justify-center",
        )}
      >
        <Link to="/" onClick={onNavigate}>
          <img
            src={isSidebarOpen ? epLogo : SidebarLogo}
            alt="EquityProtect"
            className={isSidebarOpen ? "h-10 w-auto" : "h-7 w-auto"}
          />
        </Link>
      </div>

      <nav
        className={cn(
          "flex-1 overflow-y-auto",
          isSidebarOpen ? "p-4" : "px-2 pt-4",
        )}
      >
        {renderSectionHeader("For professional use", true)}
        <ul className="space-y-1">
          {navReady ? visiblePrimaryNav.map((item) => renderLink(item)) : null}
          {navReady && !carPortfolioEnabled && !escrowOnly ? (
            <ServicerEpAwareDashboardButton
              isSidebarOpen={isSidebarOpen}
              onNavigate={onNavigate}
            />
          ) : null}
        </ul>

        {navReady && !escrowOnly
          ? renderSectionHeader("For personal use", true, "mt-6")
          : null}

        {!isSidebarOpen ? (
          <div className="my-4 border-t border-border" />
        ) : null}

        {navReady && !escrowOnly ? (
        <ul className="space-y-1">
          {(carPortfolioEnabled ? carPersonalNav : personalNav).map((item) =>
            renderLink(item),
          )}
        </ul>
        ) : null}
      </nav>

      <div
        className={cn(
          "mt-auto border-t border-border shrink-0",
          isSidebarOpen ? "p-4" : "px-2 py-3",
        )}
      >
        <CallPilotChatButton variant="subtle" isSidebarOpen={isSidebarOpen} />
      </div>
    </aside>
  );
};
